import {
  ChangeEvent,
  KeyboardEvent,
  useLayoutEffect,
  useRef,
} from 'react';

type Options = {
  /** Только цифры — то, что лежит в форме. */
  digits: string;
  /** Как показать цифры в поле: с пробелами, дефисами и т.п. */
  format: (digits: string) => string;
  maxLength: number;
  onChange: (digits: string) => void;
};

const countDigitsBefore = (text: string, position: number) =>
  text.slice(0, position).replace(/\D/g, '').length;

/** Позиция каретки в отформатированной строке сразу после `count`-й цифры. */
const caretAfterDigits = (formatted: string, count: number) => {
  if (count <= 0) return 0;

  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]) && ++seen === count) return i + 1;
  }

  return formatted.length;
};

/**
 * Поле с маской из цифр (телефон, карта), в котором каретка ведёт себя как в
 * обычном поле.
 *
 * Контролируемый input с форматированием на каждое изменение ломает каретку:
 * браузер удаляет символ там, где стоит курсор, а React затем подставляет
 * заново отформатированную строку — и WebKit уводит каретку в конец. Следующий
 * backspace удаляет уже последнюю цифру, а не ту, что под курсором. Здесь
 * положение считаем в цифрах (они не меняются от форматирования) и после
 * рендера возвращаем каретку на то же место.
 *
 * Backspace/Delete на разделителе удаляют соседнюю цифру: иначе браузер
 * стирает пробел, цифры не меняются, React возвращает пробел на место — и
 * нажатие выглядит проигнорированным.
 */
export const useDigitsMask = ({
  digits,
  format,
  maxLength,
  onChange,
}: Options) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const caretRef = useRef<number | null>(null);
  const value = format(digits);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (caretRef.current === null || !el) return;

    el.setSelectionRange(caretRef.current, caretRef.current);
    caretRef.current = null;
  });

  const commit = (
    el: HTMLInputElement,
    nextDigits: string,
    digitsBeforeCaret: number
  ) => {
    const caret = caretAfterDigits(format(nextDigits), digitsBeforeCaret);

    if (nextDigits === digits) {
      // Цифры не изменились (ввели букву, стёрли разделитель) — ререндера не
      // будет, а React всё равно вернёт в поле прежнюю строку и уведёт
      // каретку в конец. Ставим её обратно уже после этого.
      requestAnimationFrame(() => el.setSelectionRange(caret, caret));
      return;
    }

    caretRef.current = caret;
    onChange(nextDigits);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const el = event.target;
    const position = el.selectionStart ?? el.value.length;
    const next = el.value.replace(/\D/g, '').slice(0, maxLength);

    commit(el, next, Math.min(countDigitsBefore(el.value, position), next.length));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;

    const el = event.currentTarget;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;

    // Выделение стирает браузер, дальше отработает handleChange.
    if (start !== end) return;

    if (event.key === 'Backspace') {
      if (start === 0 || /\d/.test(el.value[start - 1])) return;

      event.preventDefault();
      const count = countDigitsBefore(el.value, start);
      if (count === 0) return;

      commit(el, digits.slice(0, count - 1) + digits.slice(count), count - 1);
      return;
    }

    if (start >= el.value.length || /\d/.test(el.value[start])) return;

    event.preventDefault();
    const count = countDigitsBefore(el.value, start);
    if (count >= digits.length) return;

    commit(el, digits.slice(0, count) + digits.slice(count + 1), count);
  };

  return {
    inputRef,
    value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
  };
};
